/**
 * Created by vsuthar on 8/23/21
 * File: LicenseTable
 * Project: Cloud Management Module ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { ConnectedTable, ConnectedTableLayout, TableProvider } from '@panwds/react-table'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { TableContainer } from '../../../common/components/table'
import { APP_ID_PRISMA_ACCESS, APP_ID_SDWAN } from '../../../common/constants/GlobalConstants'
import { FF_SHOW_EDIT } from '../../../common/utils/FeatureFlags'
import { setIsGlobalModuleOpen } from '../../../common/utils/Sparky'
import { getVarValue } from '../../../common/utils/Sparky'
import { getDateFormat } from '../../../subscription/util/common'
import { TABLE_HEADER_ACTIONS, TABLE_HEADER_CAPACITY, TABLE_HEADER_EXPIRATION, TABLE_HEADER_PRODUCTS, TABLE_HEADER_REGION, TABLE_HEADER_SERIAL, TABLE_HEADER_STATUS, TOOLTIP_EDIT_TENANT } from '../../constants/LabelConstants'
import { ROOT_NODE_ID } from '../../constants/TenancyConstants'
import LicenseRenderer from './cellRenderer/LicenseRenderer'
import ProductNameRenderer from './cellRenderer/ProductNameRenderer'
import StatusRenderer from './cellRenderer/StatusRenderer'
import ContractRenderer from './cellRenderer/ContractRenderer'
import ActionRenderer from './cellRenderer/ActionRenderer'
import { BUNDLED_PRODUCTS } from '../../../common/constants/LabelConstants'
import { isExpiredDate } from '../../../common/utils/Utils'
import RegionRenderer from './cellRenderer/RegionRenderer'
import { isVMFlexActivationEnabled } from '../../../common/utils/Utils'
import V5TableWrapper from '../../../common/components/table/V5TableWrapper'
import styled from 'styled-components'

const TABLE_CONTAINER = styled(V5TableWrapper)`
  [style="min-height: 289px;"] {
    height: auto !important;
  }
`

const LicenseTable = ({ data, globalDispatch, dispatch, onEditLicenses, claimInfo, supportedRegionsByKey, onDeleteInstance, onRenameInstance, ...rest }) => {

  const { updatedLicenseInfo } = rest.licenseReducer
  const { selectedTreeNode, flattenTree, auth } = rest
  const [disableEdit, setDisableEdit] = useState(false)
  const showEdit = getVarValue(FF_SHOW_EDIT, false)
  const enableProfileActivation = isVMFlexActivationEnabled(auth?.user?.supportAccountIds)

  const hideGlobalModule = useCallback(() => {
    setIsGlobalModuleOpen(false)
  }, [])

  const isSingleTenant = useMemo(() => {
    const tree = flattenTree?.filter(({ id, children }) => id !== ROOT_NODE_ID && Boolean(children?.length))
    return !tree?.length
  }, [flattenTree])

  const getExpirationDates = useCallback((row, value) => {
    const className = value && isExpiredDate(value) ? 'tw-text-error' : ''
    const { app_id, entitlements } = row?.original
    const bundledProduct = BUNDLED_PRODUCTS.has(app_id) && entitlements?.length > 1
    if (bundledProduct) {
      return (
        <div className="tw-text-sm tw-block">
          {entitlements?.map((each, index) => (
            <div
              className={isExpiredDate(each.license_expiration) ? 'tw-text-error' : ''}
              key={index}
            >{getDateFormat({}, each.license_expiration) || ''}</div>
          )
          )}
        </div>
      )
    }
    return (<div className={className}>{getDateFormat(row, value)}</div>)
  }, [])

  useEffect(() => {
    const isAmend = updatedLicenseInfo.includes(selectedTreeNode.id)
    const cgxAvailable = data.some((each) => each.app_id === APP_ID_SDWAN)
    const paeAvailable = data.find((each) => each.app_id === APP_ID_PRISMA_ACCESS)?.entitlements?.length
    const isAllInstanceComplete = data.length ? data.every(({ provisioning_status }) => provisioning_status === 'complete') : true
    if (isSingleTenant) {
      setDisableEdit(true)
    }
    else if (!showEdit) {
      setDisableEdit(() => {
        if (isAllInstanceComplete && isAmend) {
          return false
        }
        return !!((cgxAvailable && paeAvailable) || (cgxAvailable && !claimInfo?.isPaePresent) || (paeAvailable && !claimInfo?.isSDWANPresent))
      })
    }
    else {
      setDisableEdit(() => {
        if (isAllInstanceComplete && (isAmend)) {
          return false
        }
        return !(isAllInstanceComplete && (claimInfo?.isPaePresent || claimInfo?.isSDWANPresent || paeAvailable))
      })
    }

  }, [claimInfo?.isPaePresent, claimInfo?.isSDWANPresent, data, disableEdit, isSingleTenant, selectedTreeNode.id, showEdit, updatedLicenseInfo])


  const columns = useMemo(() => {
    return [
      {
        accessor: 'app_id',
        Header: TABLE_HEADER_PRODUCTS,
        // eslint-disable-next-line react/display-name
        Cell: (props) => <ProductNameRenderer hideGlobalModule={hideGlobalModule} value={props.value} globalDispatch={globalDispatch} {...props} instances={data}/>,
        width: 200
      },
      {
        accessor: 'provisioning_status',
        Header: TABLE_HEADER_STATUS,
        Cell: (props) => <StatusRenderer globalDispatch={globalDispatch}
          {...{ ...props, ...rest }} />,
        width: 160
      },
      {
        accessor: 'entitlements',
        Header: TABLE_HEADER_CAPACITY,
        Cell: LicenseRenderer,
        width: 250
      },
      {
        accessor: 'contract',
        Header: TABLE_HEADER_SERIAL,
        width: 200,
        Cell: ContractRenderer
      },
      {
        accessor: 'license_expiration',
        Header: TABLE_HEADER_EXPIRATION,
        Cell: ({ row, value }) => <div className='cm-h-full cm-w-full cm-py-2'>{getExpirationDates(row, value)}</div>,
        width: 160
      },
      {
        accessor: row => row?.platform_region || row?.region,
        Header: TABLE_HEADER_REGION,
        width: 110,
        Cell: props => <RegionRenderer {...{ supportedRegionsByKey, ...props }} />
      },
      {
        id: 'actions',
        Header: TABLE_HEADER_ACTIONS,
        width: 120,
        sticky: true,
        stickyPosition: 'right',
        Cell: (props) => <ActionRenderer selectedTreeNode={selectedTreeNode} row={props.row} onDeleteInstance={onDeleteInstance} onRenameInstance={onRenameInstance} {...rest}/>
      },
    ]
  }, [hideGlobalModule, globalDispatch, data, rest, getExpirationDates, supportedRegionsByKey, selectedTreeNode, onDeleteInstance, onRenameInstance])

  const products = data?.length || 0
  const summary = <span className="cm-text-xs cm-text-left">Displaying {products} {products > 1 ? 'products' : 'product'}</span>

  if (enableProfileActivation) {
    return (
      <TABLE_CONTAINER>
        <TableProvider
          columns={columns}
          data={data}
          enableColumnSort
          enableResizeColumns
          enableColumnOrder
        >
          <ConnectedTableLayout
            overflow
            sidecar={<div/>}
            resultsSummary={summary}
          >
            <ConnectedTable/>
          </ConnectedTableLayout>
        </TableProvider>
      </TABLE_CONTAINER>
    )
  }
  else {
    return (
      <TableContainer>
        <TABLE_CONTAINER className="cm-h-full cm-w-full">
          <TableProvider
            columns={columns}
            data={data}
            enableColumnSort
            enableResizeColumns
            enableColumnOrder
          >
            <ConnectedTableLayout
              overflow
              sidecar={<div/>}
              resultsSummary={summary}
            >
              <ConnectedTable/>
            </ConnectedTableLayout>
          </TableProvider>
        </TABLE_CONTAINER>
      </TableContainer>
    )
  }


}
export default LicenseTable
